/**
 * retry-policy — how long the product is allowed to say nothing.
 *
 * UI-13. The Related tab was reported as "renders blank". It is not: the
 * component implements loading, error, empty and ready, and every one of its
 * sixteen call sites passes all four props. What produced the blank rectangle
 * was the *shared query client*: `retry: 3` with an exponential `retryDelay`
 * reaching 30 s. A failed connections request therefore spent roughly fourteen
 * seconds re-asking before the component was ever told it had failed, and for
 * those fourteen seconds the panel showed skeletons. The defect is a policy,
 * not a missing branch.
 *
 * Two rules, both of them about honesty rather than resilience:
 *
 *   1. **A 4xx is a decision, not a hiccup.** 403 means the reader may not see
 *      this; 404 means it is not there. Asking three more times cannot change
 *      either answer — it only delays telling the reader. Never retry a 4xx.
 *   2. **A 5xx or a network fault gets at most two attempts, quickly.** Two
 *      short retries (500 ms, then 1 s) cover the genuine transient — a
 *      restarted worker, a dropped connection — and put a visible message on
 *      screen inside two seconds. The cap stays at 4 s so no future backoff
 *      arithmetic can walk it back to a fourteen-second silence.
 *
 * This file holds no react-query import: it is two pure functions the cockpit's
 * QueryClient hands to `defaultOptions.queries`, which is what lets the
 * component test assert the policy directly instead of inferring it from a
 * rendered spinner.
 */

/** The most attempts a failing request may make in total, retries included. */
export const MAX_RETRIES = 2;

/** The longest a single backoff wait may ever be, in milliseconds. */
export const MAX_RETRY_DELAY_MS = 4000;

/**
 * The HTTP status behind a thrown value, when one can be read.
 *
 * Two shapes reach us. Some callers throw an object carrying `status` (or
 * `response.status`); the module API clients throw
 * `new Error("fetch related failed: 403")` — the status is in the message and
 * nowhere else. Both are read here, in one place, so a caller never has to
 * know which shape it holds.
 *
 * Returns `undefined` when the value carries no HTTP status at all (a network
 * fault, an assertion, an aborted request) — which the retry rule treats as
 * possibly-transient rather than as a decision.
 */
export function httpErrorStatus(error: unknown): number | undefined {
  if (error == null) return undefined;

  if (typeof error === "object") {
    const rec = error as Record<string, unknown>;
    const direct = rec["status"];
    if (typeof direct === "number" && Number.isFinite(direct)) return direct;

    const response = rec["response"];
    if (response != null && typeof response === "object") {
      const nested = (response as Record<string, unknown>)["status"];
      if (typeof nested === "number" && Number.isFinite(nested)) return nested;
    }
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  // A trailing three-digit HTTP status, the shape every module client throws:
  // "fetch related failed: 403". Anchored at the end so an id that happens to
  // contain three digits cannot be mistaken for a status.
  const match = /(?:^|\D)([1-5]\d{2})\s*$/.exec(message.trim());
  if (match) {
    const parsed = Number(match[1]);
    if (parsed >= 100 && parsed <= 599) return parsed;
  }
  return undefined;
}

/** True when the failure is one the caller may not fix by asking again. */
export function isClientError(error: unknown): boolean {
  const status = httpErrorStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * The shared `retry` predicate. `failureCount` is react-query's count of
 * attempts that have already failed, so `failureCount < MAX_RETRIES` allows a
 * first request plus two retries — three requests, never four.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (isClientError(error)) return false;
  return failureCount < MAX_RETRIES;
}

/**
 * The shared `retryDelay`. 500 ms, then 1 s — so the second and final retry
 * has failed and the error is on screen inside two seconds — and hard-capped
 * at {@link MAX_RETRY_DELAY_MS} for any caller that overrides the attempt cap.
 */
export function retryDelayMs(attempt: number): number {
  const exponential = 500 * 2 ** Math.max(0, attempt);
  return Math.min(exponential, MAX_RETRY_DELAY_MS);
}

/** Total time spent waiting between attempts under the shared policy. */
export function totalRetryWaitMs(): number {
  let total = 0;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    total += retryDelayMs(attempt);
  }
  return total;
}
